import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import styles from './Dashboard.module.css';
import { useStore } from '../../hooks/useStore';
import Popup from '../../components/common-components/pop-up/Popup';
import { Tags } from 'lucide-react';
import CommonDropdown from '../../components/common-components/dropdown/CommonDropdown';
import CommonButton from '../../components/common-components/button/CommonButton';
import { hospitalService } from '../../services/modules/hospital/hospital.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ITag } from '../../models/hospital/HospitalModel';

const SelectTagsPopup = () => {
  const { popupStore, hospitalStore, hospitalFilesStore } = useStore();
  const navigate = useNavigate();
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [tags, setTags] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (popupStore.isSelectTagsOpen && hospitalStore.selectedHospitalForTags) {
      loadHospitalTags();
    }
  }, [popupStore.isSelectTagsOpen, hospitalStore.selectedHospitalForTags]);

  const loadHospitalTags = async () => {
    if (!hospitalStore.selectedHospitalForTags) return;

    setLoading(true);
    try {
      const response = await hospitalService.getTagsByHospitalId(
        hospitalStore.selectedHospitalForTags.id
      );

      if (response && Array.isArray(response)) {
        const tagOptions = response.map((tag: ITag) => ({
          value: tag.id.toString(),
          label: `${tag.prefix}`,
        }));
        setTags(tagOptions);
      } else {
        setTags([]);
        toast.warning('Bu hastane için etiket bulunamadı');
      }
    } catch (error) {
      console.error('Etiketler yüklenirken hata:', error);
      toast.error('Etiketler yüklenirken bir hata oluştu');
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = () => {
    if (!selectedTag) {
      toast.error('Lütfen bir etiket seçin');
      return;
    }

    if (!hospitalStore.selectedHospitalForTags) {
      toast.error('Hastane bilgisi bulunamadı');
      return;
    }

    // Hospital Files store'unda hastane ve etiket seç
    hospitalFilesStore.setSelectedHospital(hospitalStore.selectedHospitalForTags);

    // Seçilen etiketi bul ve set et
    const selectedTagObj = tags.find(tag => tag.value === selectedTag);
    if (selectedTagObj) {
      // API'den gelen tag listesinden seçilen tag'ı bul
      const apiResponse = hospitalService.getTagsByHospitalId(
        hospitalStore.selectedHospitalForTags.id
      );
      apiResponse.then(response => {
        if (response && Array.isArray(response)) {
          const foundTag = response.find(tag => tag.id.toString() === selectedTag);
          if (foundTag) {
            hospitalFilesStore.setSelectedTag(foundTag);
          }
        }
      });
    }

    // Hospital Files sayfasına yönlendir
    navigate('/hospital-files');
    popupStore.setIsSelectTagsOpen(false);
    setSelectedTag('');
  };

  const handleClose = () => {
    popupStore.setIsSelectTagsOpen(false);
    setSelectedTag('');
    hospitalStore.setSelectedHospitalForTags(null);
  };

  return (
    <Popup
      isOpen={popupStore.isSelectTagsOpen}
      onClose={handleClose}
      title="Etiket Seç"
      description="Hastane dosyalarına gitmek için etiket seçin"
      variant="info"
      icon={<Tags size={22} strokeWidth={2} />}
    >
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h4>Hastane: {hospitalStore.selectedHospitalForTags?.hospitalName}</h4>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Etiket Seçin:
          </label>
          <CommonDropdown
            options={tags}
            value={selectedTag}
            onChange={value => setSelectedTag(value || '')}
            placeholder={loading ? 'Yükleniyor...' : 'Etiket seçin...'}
            disabled={loading}
          />
        </div>

        <div>
          <CommonButton type="primary" onClick={handleNavigate} disabled={!selectedTag || loading}>
            Git
          </CommonButton>
        </div>
      </div>
    </Popup>
  );
};

const Dashboard = () => {
  const { dashboardStore, uiStore, popupStore, hospitalStore } = useStore();
  const { totalHospitals, totalRecords, hospitalDistribution, monthlyStats, userMonthlyStats } =
    dashboardStore;

  const CHART_COLORS = {
    line: {
      stroke: '#3B82F6',
      fill: '#93C5FD',
    },
  };

  useEffect(() => {
    uiStore.setLoading(true);
    dashboardStore.fetchDashboardStats();
    dashboardStore.fetchUserMonthlyStats();
    uiStore.setLoading(false);
  }, []);

  const handleHospitalClick = async (hospital: any) => {
    // Dashboard'daki hastane listesi sadece isim ve kayıt sayısı içeriyor
    // O yüzden hospitalStore'dan tam hastane bilgisini çekmeliyiz
    try {
      const allHospitals = await hospitalService.listAllHospitals();
      if (allHospitals && Array.isArray(allHospitals)) {
        const foundHospital = allHospitals.find(h => h.hospitalName === hospital.hospitalName);
        if (foundHospital) {
          hospitalStore.setSelectedHospitalForTags(foundHospital);
          popupStore.setIsSelectTagsOpen(true);
        } else {
          toast.error('Hastane bilgisi bulunamadı');
        }
      }
    } catch (error) {
      console.error('Hastane bilgisi alınırken hata:', error);
      toast.error('Hastane bilgisi alınamadı');
    }
  };

  const hasHospitalData = hospitalDistribution?.some(h => h.recordCount > 0);
  const hasMonthlyData = monthlyStats?.length > 0;
  const hasUserMonthlyData = userMonthlyStats?.length > 0;

  // Transform userMonthlyStats to grouped data per month with dynamic user keys
  const monthsSorted = Array.from(new Set((userMonthlyStats || []).map(i => i.month))).sort();
  const userKeys = Array.from(new Set((userMonthlyStats || []).map(i => i.userName)));
  const groupedUserMonthlyData = monthsSorted.map(month => {
    const base: any = { month };
    userKeys.forEach(user => {
      const item = userMonthlyStats.find(x => x.month === month && x.userName === user);
      base[user] = item ? item.recordCount : 0;
    });
    return base;
  });

  const BAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statTitle}>Toplam Hastane</div>
          <div className={styles.statValue}>{totalHospitals}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statTitle}>Toplam Kayıt</div>
          <div className={styles.statValue}>{totalRecords}</div>
        </div>
      </div>

      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>Hastane Bazlı Kayıt Dağılımı</h3>
          </div>
          <div className={styles.chartContainer}>
            {hasHospitalData ? (
              <div className={styles.hospitalList}>
                {hospitalDistribution.map((hospital, index) => (
                  <div
                    key={index}
                    className={styles.hospitalItem}
                    onClick={() => handleHospitalClick(hospital)}
                  >
                    <span className={styles.hospitalName}>{hospital.hospitalName}</span>
                    <span className={styles.recordCount}>{hospital.recordCount} Kayıt</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noDataContainer}>
                <p>Henüz kayıt bulunmamaktadır</p>
              </div>
            )}
          </div>
        </div>

        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>Aylık Kayıt Sayısı</h3>
          </div>
          <div className={styles.chartContainer}>
            {hasMonthlyData ? (
              <ResponsiveContainer>
                <LineChart data={monthlyStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="month" tick={{ fill: '#666' }} />
                  <YAxis tick={{ fill: '#666' }} />
                  <Tooltip
                    formatter={value => [`${value} Kayıt`]}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e0e0e0',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="recordCount"
                    stroke={CHART_COLORS.line.stroke}
                    fill={CHART_COLORS.line.fill}
                    name="Kayıt Sayısı"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noDataContainer}>
                <p>Henüz aylık kayıt bulunmamaktadır</p>
              </div>
            )}
          </div>
        </div>

        <div className={`${styles.chartCard} ${styles.fullWidth}`}>
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>Son 6 Ay Kullanıcı Bazlı Kayıt Sayısı</h3>
          </div>
          <div className={styles.chartContainer}>
            {hasUserMonthlyData ? (
              <ResponsiveContainer>
                <BarChart data={groupedUserMonthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="month" tick={{ fill: '#666' }} />
                  <YAxis tick={{ fill: '#666' }} />
                  <Tooltip />
                  <Legend />
                  {userKeys.map((user, idx) => (
                    <Bar key={user} dataKey={user} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noDataContainer}>
                <p>Son 6 aya ait kullanıcı bazlı veri bulunamadı</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {popupStore.isSelectTagsOpen && <SelectTagsPopup />}
    </div>
  );
};

export default observer(Dashboard);
